import React, { useEffect, useState, useContext } from "react";
import Skeleton from "@mui/material/Skeleton";
import CSSTransition from "react-transition-group/cjs/CSSTransition";
import { Bell, Key, Network } from "lucide-react";
import IpField from "./IpField";
import PortField from "./PortField";
import DeviceTypeField from "./DeviceTypeField";
import GroupField from "./GroupField";
import CriticalityField from "./CriticalityField";
import TagsField from "./TagsField";
import HostNameField from "./HostNameField";
import * as deviceService from "../deviceService";
import * as groupService from "../add-device-modal/groupService";
import * as domainService from "../add-device-modal/domainService";
import * as centralCredentialsService from "../add-device-modal/centralCredentialsService";
import * as ruleService from "../../rules/ruleService";
import MsbField from "./MsbField";
import ConnectivitySection from "./ConnectivitySection";
import { showSuccessToast } from "../../validation/ToastContainer";
import { put } from "../deviceService";
import { deepCopy, hasErrors, noPermission } from "../../utils";
import ChecklistSection from "./ChecklistSection";
import DualEventListSelect from "./DualEventListSelect";
import { connect } from "react-redux";
import { PopupContext } from "../../index";
import { getClosePopup } from "../../view/popup/PopupContainer";
// import { RuleExpectedCondition } from "../../vars";
import EnableSystemRemediationField from "./EnableSystemRemediationField";
import { getConfirmPopup } from "../../view/popup/PopupContainer";

function EditDeviceModalSkeleton() {
    const fieldWidths = ["92%", "88%", "95%", "90%"];
    const notificationWidths = ["82%", "76%", "84%", "74%"];

    return (
        <div className="edit-device-modal-skeleton modal-v2-form-step1">
            <section className="modal-v2-section edit-device-section edit-device-section--info">
                <div className="modal-v2-section-heading edit-device-section-header">
                    <span className="modal-v2-section-heading-icon edit-device-section-icon" />
                    <div className="edit-device-section-copy-wrap">
                        <Skeleton variant="text" width={220} height={28} />
                        <Skeleton variant="text" width={320} height={20} />
                    </div>
                </div>
                <div className="modal-v2-section-card edit-device-section-card">
                    <div className="row modal-v2-form-grid">
                        {[0, 1].map((column) => (
                            <div className="col-md-6 modal-v2-grid-field" key={column}>
                                {fieldWidths.map((width, index) => (
                                    <div className="edit-device-modal-skeleton-field" key={`${column}-${index}`}>
                                        <Skeleton variant="text" width={140} height={20} />
                                        <Skeleton variant="rectangular" width={width} height={46} className="oct_skeleton-radius-10" />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="modal-v2-section edit-device-section edit-device-section--credentials">
                <div className="modal-v2-section-heading edit-device-section-header">
                    <span className="modal-v2-section-heading-icon edit-device-section-icon" />
                    <div className="edit-device-section-copy-wrap">
                        <Skeleton variant="text" width={260} height={28} />
                        <Skeleton variant="text" width={340} height={20} />
                    </div>
                </div>
                <div className="modal-v2-section-card modal-v2-auth-card edit-device-section-card">
                    <div className="row modal-v2-form-grid">
                        <div className="col-md-6 modal-v2-grid-field">
                            {[0, 1, 2].map((index) => (
                                <div className="edit-device-modal-skeleton-field" key={`auth-left-${index}`}>
                                    <Skeleton variant="text" width={160} height={20} />
                                    <Skeleton variant="rectangular" width="94%" height={46} className="oct_skeleton-radius-10" />
                                </div>
                            ))}
                        </div>
                        <div className="col-md-6 modal-v2-grid-field">
                            <div className="edit-device-modal-skeleton-status">
                                <Skeleton variant="rectangular" width="100%" height={78} className="oct_skeleton-radius-12" />
                            </div>
                            <div className="edit-device-modal-skeleton-field">
                                <Skeleton variant="text" width={180} height={20} />
                                <Skeleton variant="rectangular" width="86%" height={46} className="oct_skeleton-radius-10" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="modal-v2-section edit-device-section edit-device-section--notifications">
                <div className="modal-v2-section-heading edit-device-section-header">
                    <span className="modal-v2-section-heading-icon edit-device-section-icon" />
                    <div className="edit-device-section-copy-wrap">
                        <Skeleton variant="text" width={220} height={28} />
                        <Skeleton variant="text" width={300} height={20} />
                    </div>
                </div>
                <div className="modal-v2-section-card edit-device-section-card">
                    <div className="row modal-v2-form-grid">
                        <div className="col-md-4 modal-v2-grid-field">
                            {notificationWidths.map((width, index) => (
                                <div className="edit-device-modal-skeleton-check" key={`notify-${index}`}>
                                    <Skeleton variant="rectangular" width="100%" height={52} className="oct_skeleton-radius-10" />
                                    <Skeleton variant="text" width={width} height={20} />
                                </div>
                            ))}
                        </div>
                        <div className="col-md-8 modal-v2-grid-field">
                            <div className="edit-device-modal-skeleton-duallist">
                                <Skeleton variant="rectangular" width="100%" height={280} className="oct_skeleton-radius-12" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function EditDeviceModal({ showModal, setShowModal, deviceId, errors, userCapability }) {

    const { setPopup } = useContext(PopupContext);

    const deviceInitialMetadata = {
        deviceTypes: [],
        groups: [],
        domains: [],
        msbList: [],
        tags: [],
        events: [],
        centralCredentials: [],
        passwordChanged: false,
        connected: null,
        connectionDataChanged: false,
    }

    const [modal, setModal] = useState(true);
    const [device, setDevice] = useState({});
    const [deviceMetadata, setDeviceMetadata] = useState(deviceInitialMetadata);
    const [loading, setLoading] = useState(true);

    const deviceTypeId = device.deviceType?.id;

    //#region Set User Capabilities
    const [modifySettingsCapability, setModifySettingsCapability] = useState(-1);
    useEffect(() => {
        if (userCapability) {
            const modifySettingsPermission = userCapability.find(item =>
                item.component === "modifydevice" && item.type === "page")?.permission;
            setModifySettingsCapability(modifySettingsPermission);
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, []);
    //#endregion

    const fetchData = async () => {
        if (showModal) {
            try {
                setLoading(true);
                let deviceMeta = {};
                let loadedDevice = {};
                const device = await deviceService.loadById(deviceId);
                loadedDevice = { ...device, sshPassword: '********', winrmPassword: '********', enablePassword: '********', powerCliPassword: '********' };

                const msbList = await ruleService.msbListByDeviceTypeId(loadedDevice.deviceType.id);
                deviceMeta = { ...deviceMetadata, msbList };

                const deviceTypes = await deviceService.loadDeviceTypes();
                deviceMeta = { ...deviceMeta, deviceTypes };

                const tags = await deviceService.tagList();
                deviceMeta = { ...deviceMeta, tags };

                const events = await deviceService.eventsList();
                deviceMeta = { ...deviceMeta, events };

                const groups = await groupService.list();
                deviceMeta = { ...deviceMeta, groups };

                const domains = await domainService.list();
                deviceMeta = { ...deviceMeta, domains };

                const centralCredentials = await centralCredentialsService.list();
                deviceMeta = { ...deviceMeta, centralCredentials };

                setDeviceMetadata({ ...deviceMetadata, ...deviceMeta });
                setDevice(loadedDevice);
                setLoading(false);
            } catch (error) {
                console.error('Error:', error);
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, [showModal]);

    useEffect(() => {
        const deviceType = device.deviceType;
        deviceType && ruleService
            .msbListByDeviceTypeId(deviceType.id)
            .then(msbList => {
                setDeviceMetadata({ ...deviceMetadata, msbList });
                if (device.msb && !msbList.find(msb => msb.id === device.msb.id)) {
                    setDevice({ ...device, msb: null });
                }
            });
    }, [deviceTypeId])

    const onDeviceChange = (value) => {
        setDevice({ ...device, ...value })
    }

    const onEnableRemediationChange = (value) => {

        if (value && value.enableRemediation) {
            const onConfirm = () => {setDevice({ ...device, ...value })}

           setPopup(
               getConfirmPopup('Caution! Enabling this setting will trigger remediation for all failed rules, which may impose impacts.',
                   'System Remediation',
                   () => onConfirm()
               )
           ); 
       } else {
            setDevice({ ...device, ...value });
       }
        
    }

    const onDeviceMetadataChange = (value) => {
        setDeviceMetadata({ ...deviceMetadata, ...value })
    }

    const onCloseClick = () => {
        setDeviceMetadata(deviceInitialMetadata);
        setShowModal(false);
    }

    const onSaveClick = async () => {

        if (modifySettingsCapability === 0) {
            noPermission();
            return;
        }
        const deviceCopy = deepCopy(device);

        if (!deviceMetadata.passwordChanged) {
            delete deviceCopy.sshPassword
            delete deviceCopy.winrmPassword
            delete deviceCopy.enablePassword
            delete deviceCopy.powerCliPassword
        }

        

        // eslint-disable-next-line no-unused-vars
     await deviceService.list().then(async allDevices => {
            const allOtherDevices = allDevices.filter(obj => obj.id !== deviceCopy.id)
            const found_ip = allOtherDevices.find(obj => obj.mngtIpAddress === deviceCopy.mngtIpAddress && obj.deviceType["id"] === deviceCopy.deviceType["id"])

            const found_name = allOtherDevices.find(obj => obj.hostName === deviceCopy.hostName);

            if (found_ip) {
                setPopup(
                    getClosePopup('Another Device With Same Ip Already Exist For This DeviceType', 'Stop Action',))
            }
            if (found_name) {
                setPopup(
                    getClosePopup('Another Device Has The Same Name', 'Stop Action',));
            }

            if (!found_ip && !found_name) {

                put(deviceCopy, deviceMetadata.passwordChanged).then(response => {
                    if (response && response.ok) {
                        showSuccessToast("Device saved", "Save");
                    }
                    return response.json();
                }).then(async updatedDevice => {
                    if (updatedDevice) {
                        await deviceService.loadById(updatedDevice.id);
                    }
                    onCloseClick();
                });

            }

        })

    }

    const isSaveButtonDisabled = () => {
        if (hasErrors(errors)) {
            return true;
        }
        if (modifySettingsCapability === 0) {
            return true;
        }
        return false;
    }

    const availableEvents = deviceMetadata.events.filter(event => {
        if (device.deviceType && event.deviceType) {
            return device.deviceType.id === event.deviceType.id
        }
        return false;
    })

    const nodeRef = React.useRef(null);
    return (
        <CSSTransition
            nodeRef={nodeRef}
            in={showModal}
            timeout={300}
            onEnter={() => setModal(true)}
            onExited={() => setModal(false)}
            unmountOnExit
            classNames="fade"
        >
            <div className="modal oct_display-block modal-v2-root" ref={nodeRef}>
                {modal &&
                    <div className="modal fade oct_modal show modal-v2-overlay">
                        <div className="modal-dialog edit-device-modal modal-xl modal-dialog-centered modal-v2-dialog" aria-modal="true">
                            <div className="modal-content modal-v2-shell">
                                <div className="sticky-header edit-device-modal-header modal-v2-header">
                                    <button type="button" className="close modal-v2-close" aria-label="Close"
                                        onClick={() => onCloseClick()}>
                                        <span aria-hidden="true">×</span>
                                    </button>
                                    <div className="modal-v2-header-top">
                                        <div className="edit-device-modal-header-copy modal-v2-header-copy">
                                            <div className="oct_form-title modal-v2-heading">Edit Device Configuration</div>
                                            <p className="edit-device-modal-subtitle modal-v2-subheading">Modify device settings, credentials, and notifications.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-body edit-device-modal-body modal-v2-body">
                                    {loading ? <EditDeviceModalSkeleton /> :
                                        <div className="edit-device-modal-form">
                                            <form className="oct_form oct_form-label-center edit-device-form-shell modal-v2-form modal-v2-form-step1">
                                                <section className="modal-v2-section edit-device-section edit-device-section--info">
                                                    <div className="modal-v2-section-heading edit-device-section-header">
                                                        <span className="modal-v2-section-heading-icon edit-device-section-icon">
                                                            <Network size={20} />
                                                        </span>
                                                        <div>
                                                            <h3 className="modal-v2-section-heading-text edit-device-section-title">Device Information</h3>
                                                            <p className="edit-device-section-copy">Update hostname, addressing, classification, and remediation behavior.</p>
                                                        </div>
                                                    </div>
                                                    <div className="modal-v2-section-card edit-device-section-card">
                                                        <div className="row modal-v2-form-grid">
                                                            <div className="col-md-6 modal-v2-grid-field">
                                                                <DeviceTypeField value={device.deviceType?.id}
                                                                    deviceTypes={deviceMetadata.deviceTypes}
                                                                    onChange={(value) => onDeviceChange({
                                                                        deviceType: value,
                                                                        events: []
                                                                    })}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />
                                                                <HostNameField value={device.hostName}
                                                                    onChange={(value) => onDeviceChange({ hostName: value })}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />
                                                                <IpField value={device.mngtIpAddress}
                                                                    onChange={(value) => {
                                                                        onDeviceChange({ mngtIpAddress: value });
                                                                        onDeviceMetadataChange({
                                                                            connected: null,
                                                                            connectionDataChanged: true
                                                                        });
                                                                    }}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />
                                                                <PortField value={device.port}
                                                                    onChange={(value) => {
                                                                        onDeviceChange({ port: value });
                                                                        onDeviceMetadataChange({
                                                                            connected: null,
                                                                            connectionDataChanged: true
                                                                        });
                                                                    }}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />      
                                                            </div>
                                                            <div className="col-md-6 modal-v2-grid-field">
                                                                <GroupField value={device.group?.id}
                                                                    groups={deviceMetadata.groups}
                                                                    onChange={(value) => onDeviceChange({ group: value })}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />
                                                                <CriticalityField value={device.criticality}
                                                                    onChange={(value) => onDeviceChange({ criticality: value })}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />
                                                                <TagsField tags={deviceMetadata.tags}
                                                                    selectedTags={device.tags || []}
                                                                    onChange={(value) => onDeviceChange({ tags: value })}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />
                                                                <MsbField value={device.msb ? device.msb.id : null}
                                                                    msbList={deviceMetadata.msbList}
                                                                    onChange={(value) => onDeviceChange({ msb: value })}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />
                                                            </div>
                                                            <div className="col-12 modal-v2-grid-field">
                                                                <div className="edit-device-remediation-field">
                                                                    <EnableSystemRemediationField value={device.enableRemediation}
                                                                                                onChange={(value) => onEnableRemediationChange({ enableRemediation: value })}
                                                                                                disabled={modifySettingsCapability === 0 ? true : false} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>

                                                <section className="modal-v2-section edit-device-section edit-device-section--credentials">
                                                    <div className="modal-v2-section-heading edit-device-section-header">
                                                        <span className="modal-v2-section-heading-icon edit-device-section-icon">
                                                            <Key size={20} />
                                                        </span>
                                                        <div>
                                                            <h3 className="modal-v2-section-heading-text edit-device-section-title">Authentication &amp; Credentials</h3>
                                                            <p className="edit-device-section-copy">Manage connection method, credentials, and connectivity verification.</p>
                                                        </div>
                                                    </div>
                                                    <div className="modal-v2-section-card modal-v2-auth-card edit-device-section-card">
                                                        <ConnectivitySection device={device}
                                                            setDevice={setDevice}
                                                            deviceMetadata={deviceMetadata}
                                                            onDeviceMetadataChange={onDeviceMetadataChange}
                                                            onDeviceChange={onDeviceChange}
                                                            disabled={modifySettingsCapability === 0 ? true : false} />
                                                    </div>
                                                </section>

                                                <section className="modal-v2-section edit-device-section edit-device-section--notifications">
                                                    <div className="modal-v2-section-heading edit-device-section-header">
                                                        <span className="modal-v2-section-heading-icon edit-device-section-icon">
                                                            <Bell size={20} />
                                                        </span>
                                                        <div>
                                                            <h3 className="modal-v2-section-heading-text edit-device-section-title">Notification Settings</h3>
                                                            <p className="edit-device-section-copy">Control processing, alerting, and event selection for this device.</p>
                                                        </div>
                                                    </div>
                                                    <div className="modal-v2-section-card edit-device-section-card">
                                                        <div className="row modal-v2-form-grid">
                                                            <div className="col-md-4 modal-v2-grid-field">
                                                                <ChecklistSection device={device}
                                                                    onDeviceChange={onDeviceChange}
                                                                    disabled={modifySettingsCapability === 0 ? true : false} />
                                                            </div>
                                                            <div className="col-md-8 modal-v2-grid-field">
                                                                <div className="modal-v2-form-step3">
                                                                    <DualEventListSelect events={availableEvents}
                                                                        selectedEvents={device.events || []}
                                                                        enableSyslogProcessing={device.enableSyslogProcessing}
                                                                        onChange={value => onDeviceChange({ events: value })}
                                                                        disabled={modifySettingsCapability === 0 ? true : false} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                            </form>
                                        </div>
                                    }
                                </div>
                                {!loading &&
                                    <div className="modal-footer edit-device-modal-footer modal-v2-footer">
                                        <div className="edit-device-modal-footer-note modal-v2-footer-note">
                                            <span className="modal-v2-footer-note-star">*</span> Required fields
                                        </div>
                                        <div className="edit-device-modal-footer-actions modal-v2-footer-actions">
                                            <button type="button" className="btn oct_btn-secondary modal-v2-btn modal-v2-btn-outline"
                                                onClick={() => onCloseClick()}>Cancel</button>
                                            <button type="button" className="btn oct_btn-primary modal-v2-btn modal-v2-btn-primary"
                                                disabled={isSaveButtonDisabled()}
                                                onClick={() => onSaveClick()}>Save Changes</button>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
        </CSSTransition>

    );
}

function mapStateToProps(state) {
    const auth = state.auth;
    return {
        errors: state.validation,
        userCapability: auth.userCapability,
    }
}

export default connect(mapStateToProps)(EditDeviceModal);